import {
  FC,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  PropsWithChildren,
} from 'react';
import { useAuth } from 'context/auth';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';
import useGetActiveCrmPushes from 'hooks/services/crmPush/useGetActiveCrmPushes';
import {
  CrmPush,
  CrmPushPair,
  AsynchronousOperationStatus,
} from 'crono-fe-common/types/crmPush';
import { CrmPushContext, CrmPushActionsContext } from './contexts';

// Stable empty arrays — returned by the derived useMemos when there are no
// active pushes, so their references stay the same across fetches and
// CrmPushContext consumers skip re-renders.
const EMPTY_ACCOUNT_PAIRS: (CrmPushPair & {
  status: AsynchronousOperationStatus;
})[] = [];
const EMPTY_PROSPECT_PAIRS: (CrmPushPair & {
  status: AsynchronousOperationStatus;
})[] = [];
const EMPTY_WARNINGS: CrmPush[] = [];

const CrmPushProvider: FC<PropsWithChildren> = ({ children }) => {
  const { enabledContextCalls } = useAuth();
  const { hasAppAccess } = useRoleCapabilities();
  const [shouldPoll, setShouldPoll] = useState(true);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const { data, mutate } = useGetActiveCrmPushes();

  const fetchData = useCallback(() => {
    if (enabledContextCalls && hasAppAccess) {
      mutate();
    }
  }, [enabledContextCalls, hasAppAccess, mutate]);

  // Initial fetch when component mounts
  useEffect(() => {
    if (enabledContextCalls && shouldPoll) {
      fetchData();
    }
  }, [enabledContextCalls, shouldPoll, fetchData]);

  // Set up polling interval
  useEffect(() => {
    if (shouldPoll && enabledContextCalls && hasAppAccess) {
      pollingIntervalRef.current = setInterval(() => {
        fetchData();
      }, 10000); // 10 seconds
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [shouldPoll, enabledContextCalls, hasAppAccess, fetchData]);

  useEffect(() => {
    // Stop polling if response is empty or no data
    if (data?.data?.data && Array.isArray(data.data.data)) {
      const hasActivePushes = data.data.data.length > 0;
      if (!hasActivePushes && shouldPoll) {
        setShouldPoll(false);
      } else if (hasActivePushes && !shouldPoll) {
        setShouldPoll(true);
      }
    }
  }, [data, shouldPoll]);

  const resumePolling = useCallback(() => {
    setShouldPoll(true);
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const accountPushPairs = useMemo(() => {
    const pushes = data?.data?.data;
    if (!pushes?.length) return EMPTY_ACCOUNT_PAIRS;
    const accountPushes = pushes.filter((push) => push.tableType === 'Account');
    if (accountPushes.length === 0) return EMPTY_ACCOUNT_PAIRS;
    return accountPushes.flatMap((push) =>
      push.crmPushPairs.map((pair) => ({ ...pair, status: push.status })),
    );
  }, [data]);

  const prospectPushPairs = useMemo(() => {
    const pushes = data?.data?.data;
    if (!pushes?.length) return EMPTY_PROSPECT_PAIRS;
    const prospectPushes = pushes.filter(
      (push) => push.tableType === 'Prospect',
    );
    if (prospectPushes.length === 0) return EMPTY_PROSPECT_PAIRS;
    return prospectPushes.flatMap((push) =>
      push.crmPushPairs.map((pair) => ({ ...pair, status: push.status })),
    );
  }, [data]);

  const warnings = useMemo(() => {
    const pushes = data?.data?.data;
    if (!pushes?.length) return EMPTY_WARNINGS;

    const errorPushes = pushes.filter(
      (push) => push.status === AsynchronousOperationStatus.CompletedWithErrors,
    );
    if (errorPushes.length === 0) return EMPTY_WARNINGS;

    const activeOldObjectIds = new Set<string>();
    pushes.forEach((push) => {
      if (push.status !== AsynchronousOperationStatus.CompletedWithErrors) {
        push.crmPushPairs.forEach((pair) => {
          activeOldObjectIds.add(pair.oldObjectId);
        });
      }
    });

    // If there is another active push -> we are trying again right now and old warning is irrelevant
    const result = errorPushes.filter((errorPush) => {
      return !errorPush.crmPushPairs.some((pair) =>
        activeOldObjectIds.has(pair.oldObjectId),
      );
    });
    return result.length === 0 ? EMPTY_WARNINGS : result;
  }, [data]);

  const dataValue = useMemo(
    () => ({ refetch, accountPushPairs, prospectPushPairs, warnings }),
    [refetch, accountPushPairs, prospectPushPairs, warnings],
  );

  const actionsValue = useMemo(() => ({ resumePolling }), [resumePolling]);

  return (
    <CrmPushActionsContext.Provider value={actionsValue}>
      <CrmPushContext.Provider value={dataValue}>
        {children}
      </CrmPushContext.Provider>
    </CrmPushActionsContext.Provider>
  );
};

export default CrmPushProvider;

export function useCrmPush() {
  const context = useContext(CrmPushContext);
  if (context === undefined) {
    throw new Error(`useCrmPush must be used within a CrmPushProvider`);
  }
  return context;
}

export function useCrmPushActions() {
  const context = useContext(CrmPushActionsContext);
  if (context === undefined) {
    throw new Error(`useCrmPushActions must be used within a CrmPushProvider`);
  }
  return context;
}
